import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Box, Grid, Typography, Avatar, Skeleton, Chip, Divider,
  Snackbar, Alert, Stack, IconButton, Tooltip
} from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import { useNextGenAuth } from '../context/NextGenAuthContext';
import API_BASE_URL from '../../Config/config';
import NextGenEditProfileDrawer from '../components/NextGenEditProfileDrawer';
import {
  TOKENS, PageContainer, PageHeader, SectionCard, SectionTitle,
  GradientButton, StatusChip, fadeUp,
} from '../components/NextGenPortalUI';

const getInitials = (name) => {
  if (!name) return 'U';
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
};

const InfoField = ({ icon, label, value, loading }) => (
  <Box
    sx={{
      p: 1.75, borderRadius: '12px',
      background: TOKENS.bg.page,
      border: `1px solid ${TOKENS.border}`,
      height: '100%',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.6 }}>
      {React.cloneElement(icon, { sx: { fontSize: 13, color: TOKENS.brand.primaryLight } })}
      <Typography
        variant="caption"
        sx={{ color: TOKENS.text.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.65rem' }}
      >
        {label}
      </Typography>
    </Box>
    {loading ? (
      <Skeleton variant="text" width="70%" height={20} />
    ) : (
      <Typography variant="body2" sx={{ color: TOKENS.text.primary, fontWeight: 700, fontSize: '0.9rem' }}>
        {value || '—'}
      </Typography>
    )}
  </Box>
);

const CopyField = ({ label, value, loading, isLink }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShare = () => {
    if (navigator.share && isLink) {
      navigator.share({ title: 'Join NextGen Builder', url: value });
    } else {
      handleCopy();
    }
  };

  return (
    <Box
      sx={{
        p: 1.75, borderRadius: '12px',
        background: 'rgba(108,59,255,0.04)',
        border: '1px solid rgba(108,59,255,0.15)',
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: TOKENS.text.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.65rem' }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
        {loading ? (
          <Skeleton variant="text" width="65%" sx={{ flex: 1 }} />
        ) : (
          <Typography
            variant="body2"
            sx={{
              flex: 1, fontFamily: '"Plus Jakarta Sans", monospace', fontWeight: 700,
              color: TOKENS.brand.primary, wordBreak: 'break-all', fontSize: '0.85rem', letterSpacing: isLink ? 0 : '0.04em',
            }}
          >
            {value || '—'}
          </Typography>
        )}
        <Tooltip title={copied ? 'Copied!' : 'Copy'} arrow>
          <IconButton
            onClick={handleCopy}
            size="small"
            sx={{
              color: copied ? '#16A34A' : TOKENS.brand.primary,
              bgcolor: copied ? 'rgba(22,163,74,0.1)' : 'rgba(108,59,255,0.08)',
              border: `1px solid ${copied ? 'rgba(22,163,74,0.25)' : 'rgba(108,59,255,0.2)'}`,
              borderRadius: '8px',
              p: 0.6,
              transition: 'all 0.2s',
              '&:hover': { bgcolor: copied ? 'rgba(22,163,74,0.15)' : 'rgba(108,59,255,0.15)' },
            }}
          >
            {copied ? <CheckRoundedIcon sx={{ fontSize: 15 }} /> : <ContentCopyRoundedIcon sx={{ fontSize: 15 }} />}
          </IconButton>
        </Tooltip>
        {isLink && (
          <Tooltip title="Share" arrow>
            <IconButton
              onClick={handleShare}
              size="small"
              sx={{
                color: TOKENS.brand.primary,
                bgcolor: 'rgba(108,59,255,0.08)',
                border: '1px solid rgba(108,59,255,0.2)',
                borderRadius: '8px',
                p: 0.6,
                '&:hover': { bgcolor: 'rgba(108,59,255,0.15)' },
              }}
            >
              <ShareRoundedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const NextGenProfile = () => {
  const { nextgen_builder } = useNextGenAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [isEditOpen, setIsEditOpen] = useState(false);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/profile`);
      setProfile(res.data.data);
    } catch (err) {
      // 'Builder session required' is now handled globally — the shared
      // axios interceptor retries once (covers the hard-reload race) and,
      // on a genuine failure, fires the SessionExpiredModal popup that
      // NextGenLayout renders. Showing this page's own raw inline alert
      // on top of that popup would be redundant, so it's skipped here;
      // any OTHER error (a real network/server failure) still shows as
      // before.
      const message = err.response?.data?.message;
      if (message !== 'Builder session required') {
        setError(message || 'Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleUpdateSuccess = (message) => {
    setSnack({ open: true, message, severity: 'success' });
    fetchProfile();
  };

  const isSubscriptionActive = profile?.subscriptionActive;
  const validTillDate = profile?.subscription_valid_till
    ? new Date(profile.subscription_valid_till).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
      })
    : null;

  return (
    <motion.div {...fadeUp}>
      <PageContainer maxWidth={1400}>

        <PageHeader
          icon={<PersonRoundedIcon />}
          eyebrow="Account"
          title="My Profile"
          description="Manage your account details and subscription"
        />

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Stack spacing={3} sx={{ maxWidth: 1200, mx: 'auto' }}>

          {/* ── Hero: Avatar + Identity + Edit ── */}
          <SectionCard padding={{ xs: 2.75, md: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: { xs: 2.5, md: 3.5 },
              }}
            >
              <Avatar
                src={profile?.profileImage || profile?.profile_image || undefined}
                sx={{
                  width: { xs: 104, md: 136 },
                  height: { xs: 104, md: 136 },
                  fontSize: { xs: '2rem', md: '2.6rem' },
                  fontWeight: 800,
                  background: TOKENS.brand.gradient,
                  border: '4px solid #FFFFFF',
                  outline: `1px solid ${TOKENS.border}`,
                  boxShadow: '0 10px 28px rgba(108,59,255,0.28)',
                  flexShrink: 0,
                }}
              >
                {loading ? '' : getInitials(profile?.fullName || profile?.full_name)}
              </Avatar>

              <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
                {loading ? (
                  <Stack spacing={0.75}>
                    <Skeleton variant="text" width={220} height={38} />
                    <Skeleton variant="text" width={140} height={24} />
                    <Skeleton variant="rectangular" width={160} height={26} sx={{ borderRadius: '8px', mt: 0.5 }} />
                  </Stack>
                ) : (
                  <>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 800, color: TOKENS.text.primary, letterSpacing: '-0.02em', fontSize: { xs: '1.4rem', md: '1.85rem' } }}
                    >
                      {profile?.fullName || profile?.full_name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: TOKENS.text.secondary, fontWeight: 600, mt: 0.25, mb: 1.25 }}>
                      @{profile?.username}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                      {isSubscriptionActive ? (
                        <StatusChip
                          tone="success"
                          icon={<VerifiedRoundedIcon />}
                          label={`${(profile?.subscription_plan || '').toUpperCase()} Member`}
                        />
                      ) : (
                        <StatusChip
                          label="Free Account"
                          sx={{ bgcolor: '#F1F5F9', color: TOKENS.text.secondary, border: `1px solid ${TOKENS.border}` }}
                        />
                      )}
                      {isSubscriptionActive && validTillDate && (
                        <Chip
                          icon={<CalendarTodayRoundedIcon sx={{ fontSize: '14px !important' }} />}
                          label={`Valid till ${validTillDate}`}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(108,59,255,0.06)', color: TOKENS.brand.primary,
                            border: '1px solid rgba(108,59,255,0.2)', fontWeight: 700, fontSize: '0.72rem',
                            '& .MuiChip-icon': { color: TOKENS.brand.primary },
                          }}
                        />
                      )}
                      <Chip
                        icon={<AccountBalanceWalletRoundedIcon sx={{ fontSize: '14px !important' }} />}
                        label={`Wallet ₹${(profile?.walletBalance || 0).toLocaleString('en-IN')}`}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(22,163,74,0.08)', color: '#16A34A',
                          border: '1px solid rgba(22,163,74,0.22)', fontWeight: 700, fontSize: '0.72rem',
                          '& .MuiChip-icon': { color: '#16A34A' },
                        }}
                      />
                    </Stack>
                  </>
                )}
              </Box>

              <GradientButton
                onClick={() => setIsEditOpen(true)}
                startIcon={<EditRoundedIcon sx={{ fontSize: '17px !important' }} />}
                endIcon={null}
                sx={{ flexShrink: 0 }}
              >
                Edit Profile
              </GradientButton>
            </Box>
          </SectionCard>

          {/* ── Personal Information ── */}
          <SectionCard padding={{ xs: 2.5, md: 3.5 }}>
            <SectionTitle icon={<PersonRoundedIcon />} title="Personal Information" />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <InfoField icon={<PersonRoundedIcon />} label="Full Name" value={profile?.fullName || profile?.full_name} loading={loading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoField icon={<PersonRoundedIcon />} label="Username" value={`@${profile?.username || ''}`} loading={loading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoField icon={<EmailRoundedIcon />} label="Email Address" value={profile?.email} loading={loading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoField icon={<PhoneRoundedIcon />} label="Phone Number" value={profile?.mobileNumber || profile?.mobile_number} loading={loading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoField icon={<SchoolRoundedIcon />} label="College" value={profile?.college} loading={loading} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoField icon={<LocationOnRoundedIcon />} label="City" value={profile?.city} loading={loading} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoField icon={<LocationOnRoundedIcon />} label="State" value={profile?.state} loading={loading} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoField icon={<LocationOnRoundedIcon />} label="Country" value={profile?.country} loading={loading} />
              </Grid>
            </Grid>
          </SectionCard>

          {/* ── Subscription ── */}
          <SectionCard padding={{ xs: 2.5, md: 3.5 }}>
            <SectionTitle
              icon={<VerifiedRoundedIcon />}
              title="Subscription Details"
            />
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4}>
                <InfoField icon={<VerifiedRoundedIcon />} label="Plan" value={profile?.subscription_plan ? profile.subscription_plan.toUpperCase() : 'Free'} loading={loading} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <InfoField icon={<CheckRoundedIcon />} label="Status" value={isSubscriptionActive ? 'Active' : 'Inactive'} loading={loading} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <InfoField icon={<CalendarTodayRoundedIcon />} label="Valid Till" value={validTillDate || 'N/A'} loading={loading} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <InfoField
                  icon={<WorkRoundedIcon />}
                  label="Versions Allowed"
                  value={profile?.subscription_versions_allowed ? `${profile.subscription_versions_allowed} Version(s)` : 'N/A'}
                  loading={loading}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <InfoField icon={<WorkRoundedIcon />} label="Current Version" value={profile?.project_version ? `v${profile.project_version}` : 'N/A'} loading={loading} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <InfoField
                  icon={<AccountBalanceWalletRoundedIcon />}
                  label="Wallet Balance"
                  value={loading ? '' : `₹${(profile?.walletBalance || 0).toLocaleString('en-IN')}`}
                  loading={loading}
                />
              </Grid>
            </Grid>
          </SectionCard>

          {/* ── Refer & Earn ── */}
          <SectionCard padding={{ xs: 2.5, md: 3.5 }}>
            <SectionTitle icon={<CardGiftcardRoundedIcon />} title="Refer & Earn" />
            <Stack spacing={1.5}>
              <CopyField label="Your Referral Code" value={profile?.referral_code} loading={loading} isLink={false} />
              <CopyField label="Your Referral Link" value={profile?.referral_link} loading={loading} isLink />
            </Stack>
            <Divider sx={{ my: 1.75, borderColor: TOKENS.borderSoft }} />
            <Typography variant="caption" sx={{ color: TOKENS.text.muted, display: 'block', lineHeight: 1.6 }}>
              Share your referral code with friends. They save on their first subscription, and you earn wallet credits!
            </Typography>
          </SectionCard>

        </Stack>

        <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          <Alert severity={snack.severity}>{snack.message}</Alert>
        </Snackbar>

        <NextGenEditProfileDrawer
          open={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          userData={profile}
          onUpdateSuccess={handleUpdateSuccess}
        />
      </PageContainer>
    </motion.div>
  );
};

export default NextGenProfile;
